import api from './api';

/**
 * In-flight + session cache for reference GETs (same-tab).
 * Eliminates duplicate /agent_select, /filters/default, etc. when many
 * components mount together (Leads page + modals + Strict Mode).
 */

/** Clear cached reference responses (e.g. after logout). Call after definitions load. */
export function clearLeadsReferenceDataCache() {
  _agentSelectCached.reset?.();
  _leadTagsCached.reset?.();
  _projectUnitSelectCached.reset?.();
  _leadsDefaultFiltersCached.reset?.();
  _callsToolbarDefaultFiltersCached.reset?.();
  _integrationsOverviewCached.reset?.();
}

const _agentSelectCached = (() => {
  let cache = null;
  let inflight = null;
  const reset = () => {
    cache = null;
    inflight = null;
  };
  return Object.assign(
    async () => {
      if (cache !== null) return cache;
      if (inflight) return inflight;
      inflight = (async () => {
        try {
          const response = await api.get('/lms/agent_select/');
          cache = response.data;
          return cache;
        } catch (error) {
          console.error('Error fetching agent select options:', error);
          cache = [];
          return cache;
        }
      })().finally(() => {
        inflight = null;
      });
      return inflight;
    },
    { reset },
  );
})();

/**
 * Fetch agents for select dropdown options
 * @param {Object} _params - Unused (kept for API compatibility)
 * @returns {Promise} - Promise that resolves to agent data
 */
export const fetchAgentSelect = async (_params = {}) => {
  return _agentSelectCached();
};


/**
 * Fetch leads for select dropdown options
 * @param {Object} params - Optional query parameters
 * @returns {Promise} - Promise that resolves to lead data
 */
export const fetchLeadSelect = async (params = {}) => {
  try {
    // Build query parameters for account filtering
    const queryParams = new URLSearchParams();
    
    if (params.account_id) {
      queryParams.append('account_id', params.account_id);
    }
    
    if (params.user_id) {
      queryParams.append('user_id', params.user_id);
    }
    
    const url = `/lms/leads_select/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching lead select options:', error);
    return [];
  }
};

/**
 * Fetch developers for select dropdown options
 * @param {Object} params - Optional query parameters
 * @returns {Promise} - Promise that resolves to developer data
 */
export const fetchDeveloperSelect = async(params = {}) => {
    try {
        const response = await api.get('/lms/developer_list/');
        return response.data;
    } catch (error) {
        console.error('Error fetching developer select options:', error);
        throw error;
    }
}


/**
 * Fetch projects for select dropdown options
 * @param {Object} params - Optional query parameters
 * @returns {Promise} - Promise that resolves to project data
 */
export const fetchProjectSelect = async (params = {}) => {
  try {
    const response = await api.get('/lms/projects_select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching project select options:', error);
    throw error;
  }
};

/**
 * Fetch configuration options for select dropdown
 * @param {string} project_id - ID of the project to fetch configurations for
 * @param {Object} params - Optional query parameters
 * @returns {Promise} - Promise that resolves to configuration data
 */
export const fetchConfigurationSelect = async (project_id) => {
  try {
    const response = await api.get(`/lms/configurations_select/${project_id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching configurations for project ${project_id}:`, error);
    return [];
  }
};


/**
 * Fetch unit options for select dropdown
 * @param {number} project_id - The ID of the project
 * @returns {Promise} - Promise that resolves to unit options
 */
export const fetchUnitSelect = async (project_id) => {
  try {
    const response = await api.get(`/lms/units_select/${project_id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching unit select options:', error);
    throw error;
  }
};

/**
 * Fetch site visit status options for select dropdown
 * @returns {Promise} - Promise that resolves to site visit status options
 */
export const fetchSiteVisitStatusOptions = async () => {
  try {
    const response = await api.get('/lms/sitevisit_status_select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching site visit status options:', error);
    throw error;
  }
};

/**
 * Fetch transportation type options for select dropdown
 * @returns {Promise} - Promise that resolves to transportation type options
 */
export const fetchTransportationTypeOptions = async () => {
  try {
        const response = await api.get('/lms/transportation_type_select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching transportation type options:', error);
    throw error;
  }
};

/**
 * Fetch visit type options for select dropdown
 * @returns {Promise} - Promise that resolves to visit type options
 */
export const fetchVisitTypeOptions = async () => {
  try {
    const response = await api.get('/lms/visit_type_select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching visit type options:', error);
    throw error;
  }
};

/**
 * Fetch site visit status templates
 * @returns {Promise} - Promise that resolves to the site visit status templates
 */
export const fetchSiteVisitStatusTemplates = async () => {
  try {
    const response = await api.get('/lms/site-visits/status-templates/');
    return response.data;
  } catch (error) {
    console.error('Error fetching site visit status templates:', error);
    throw error;
  }
};

export const fetchBookingStatusSelect = async () => {
  try {
    const response = await api.get('/bms/booking-status-select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching booking status select:', error);
    throw error;
  }
};

export const fetchPaymentStatusSelect = async () => {
  try {
    const response = await api.get('/bms/payment-status-select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching payment status select:', error);
    throw error;
  }
};


export const fetchPaymentModeSelect = async () => {
  try {
    const response = await api.get('/bms/payment-mode-select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching payment mode select:', error);
    throw error;
  }
};

export const fetchBookingStatusTemplates = async () => {
  try {
    const response = await api.get('/bms/booking-status-templates/');
    return response.data;
  } catch (error) {     
    console.error('Error fetching booking status templates:', error);
    throw error;
  }
};


export const fetchCommitionStatusSelectTemplates = async () => {
  try {
    const response = await api.get('/bms/commition-status-select-templates/');
    return response.data;
  } catch (error) {
    console.error('Error fetching commition status select:', error);
    throw error;
  }
};


export const fetchPaymentStatusSelectTemplates = async () => {
  try {
    const response = await api.get('/bms/payment-status-select-templates/');
    return response.data;
  } catch (error) { 
    console.error('Error fetching payment status select templates:', error);
    throw error;
  }
};

export const fetchBookingLeadSelect = async (search = '', limit = 100) => {
  try {
    const params = new URLSearchParams();
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    if (limit) {
      params.append('limit', limit.toString());
    }
    const url = `/bms/booking-lead-select/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking lead select:', error);
    throw error;
  }
};


const _projectUnitSelectCached = (() => {
  let cache = null;
  let inflight = null;
  const reset = () => {
    cache = null;
    inflight = null;
  };
  return Object.assign(
    async () => {
      if (cache !== null) return cache;
      if (inflight) return inflight;
      inflight = (async () => {
        const response = await api.get('/bms/project-unit-select/');
        cache = response.data;
        return cache;
      })().catch((error) => {
        console.error('Error fetching project unit select:', error);
        cache = null;
        throw error;
      }).finally(() => {
        inflight = null;
      });
      return inflight;
    },
    { reset },
  );
})();

export const fetchProjectUnitSelect = async () => {
  return _projectUnitSelectCached();
};

export const fetchOtherChargesSelect = async () => {
  try {
    const response = await api.get('/bms/other-charges-select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching other charges select:', error);
    throw error;
  }
};

export const fetchCommissionStatusSelect = async () => {
  try {
    const response = await api.get('/bms/commission-status-select/');
    return response.data;
  } catch (error) {
    console.error('Error fetching commission status select:', error);
    throw error;
  }
};

export const fetchBookingDocumentTypeSelect = async () => {
  try {
    const response = await api.get('/bms/bookings/document-types/');
    return response.data;
  } catch (error) { 
    console.error('Error fetching booking document type select:', error);
    throw error;
  }
};


/**
 * Fetch leads default filters
 * @returns {Promise} - Promise that resolves to leads default filters
 */
const _leadsDefaultFiltersCached = (() => {
  let cache = null;
  let inflight = null;
  const reset = () => {
    cache = null;
    inflight = null;
  };
  return Object.assign(
    async () => {
      if (cache !== null) return cache;
      if (inflight) return inflight;
      inflight = (async () => {
        try {
          const response = await api.get('/lms/leads/filters/default/');
          cache = response.data;
          return cache;
        } catch (error) {
          console.error('Error fetching leads default filters:', error);
          cache = {};
          return cache;
        }
      })().finally(() => {
        inflight = null;
      });
      return inflight;
    },
    { reset },
  );
})();

export const fetchLeadsDefaultFilters = async () => {
  return _leadsDefaultFiltersCached();
};


/**
 * Fetch stage status flow
 * @returns {Promise} - Promise that resolves to stage status flow
 */
export const fetchStageStatusFlow = async () => {
  try {
    const response = await api.get('/lms/lead-status-flow/');
    return response.data;
  } catch (error) {
    console.error('Error fetching stage status flow:', error);
    return [];
  }
};


export const fetchLeadNoteTemplates = async () => {
  try {
    const response = await api.get('/lms/lead-note-templates/');
    return response.data;
  } catch (error) {
    console.error('Error fetching lead note templates:', error);
    return null;
  }
};

const _leadTagsCached = (() => {
  let cache = null;
  let inflight = null;
  const reset = () => {
    cache = null;
    inflight = null;
  };
  return Object.assign(
    async () => {
      if (cache !== null) return cache;
      if (inflight) return inflight;
      inflight = (async () => {
        try {
          const response = await api.get('/lms/lead/tags/');
          cache = response.data;
          return cache;
        } catch (error) {
          console.error('Error fetching lead tags:', error);
          cache = [];
          return cache;
        }
      })().finally(() => {
        inflight = null;
      });
      return inflight;
    },
    { reset },
  );
})();

export const fetchLeadTags = async () => {
  return _leadTagsCached();
};

const _integrationsOverviewCached = (() => {
  let cache = null;
  let inflight = null;
  const reset = () => {
    cache = null;
    inflight = null;
  };
  return Object.assign(
    async () => {
      if (cache !== null) return cache;
      if (inflight) return inflight;
      inflight = (async () => {
        try {
          const response = await api.get('/lms/integrations/');
          const data = Array.isArray(response.data) ? response.data : [];
          cache = data;
          return cache;
        } catch (error) {
          console.error('Error fetching integrations:', error);
          cache = [];
          return cache;
        }
      })().finally(() => {
        inflight = null;
      });
      return inflight;
    },
    { reset },
  );
})();

/** Cached GET /lms/integrations/ — shared by layout + settings-style checks */
export const fetchIntegrationsOverview = async () => {
  return _integrationsOverviewCached();
};


export const checkNewRequestAllowed = async () => {
  try {
    const response = await api.get('subscription/new_request_allowed/');
    if (response.status === 200) {
      return response.data.allowed !== false;
    }
    return false;
  } catch (error) {
    return false;
  }
};



/** Same-tab cache for GET /lms/calls/filters/default/ (Calls page + Strict Mode remounts). */
const _callsToolbarDefaultFiltersCached = (() => {
  let cache = null;
  let inflight = null;
  const reset = () => {
    cache = null;
    inflight = null;
  };
  return Object.assign(
    async () => {
      if (cache !== null) return cache;
      if (inflight) return inflight;
      inflight = (async () => {
        try {
          const response = await api.get('/lms/calls/filters/default/');
          cache = response.data;
          return cache;
        } catch (error) {
          console.error('Error fetching calls default filters:', error);
          cache = [];
          return cache;
        } finally {
          inflight = null;
        }
      })();
      return inflight;
    },
    { reset },
  );
})();

/**
 * Fetch calls toolbar default filters (e.g. agent list for CallsToolbar).
 * @returns {Promise} - Promise that resolves to filter options payload
 */
export const fetchCallsDefaultFilters = async () => _callsToolbarDefaultFiltersCached();


export const fetchBookingFilters = async () => { 
  try {
    console.log('Making API call to /bms/booking/filters/');
    const response = await api.get('/bms/booking/filters/');
    console.log('API response:', response);
    console.log('API response.data:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking filters:', error);
    // Return empty data instead of throwing error to prevent component crash
    return {
      agents: [],
      projects: [],
      leads: [],
      booking_statuses: [],
      payment_statuses: [],
      payment_modes: [],
      units: {},
      filtered_projects: [],
      other_charges: []
    };
  }
};


const mapProjectsToOptions = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((project) => ({
    value: project.id,
    label: project.name ?? project.label ?? '—',
    id: project.id
  }));
};

/**
 * Fetches project/property options for dropdowns (e.g. Add Properties to Lead).
 * Uses salesgro_inventory: GET /api/inventory/options/ (projects key).
 */
export const getPropertyOptions = async () => {
  try {
    const response = await api.get('/inventory/options/');
    const data = response?.data;
    const list = data?.projects;
    const projects = Array.isArray(list) ? mapProjectsToOptions(list) : [];
    if (projects.length > 0) return projects;
  } catch (error) {
    console.warn('Property options (inventory/options) failed, trying inventory projects fallback:', error?.message);
  }
  try {
    const fallback = await api.get('/inventory/projects/');
    const list = fallback?.data;
    const projects = Array.isArray(list) ? mapProjectsToOptions(list) : [];
    return projects;
  } catch (fallbackError) {
    console.error('Error fetching property options:', fallbackError);
    return [];
  }
};
